<script setup lang="ts">
import ScoreRating from '@/components/Conversations/Application/ScoreRating.vue';
import ApplicationScoreCompactDisplay from '@/components/Conversations/Application/ApplicationScoreCompactDisplay.vue';
import { computed } from 'vue';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';

const props = withDefaults(
  defineProps<{
    isSelected: boolean;
    shouldBgBeWhite?: boolean;
    jobTitle?: string;
    hideJobTitle?: boolean;
    score?: number | null;
    isViewed?: boolean;
    variant?: 'stars' | 'compact';
    /** Compact variant only: 2px of wrapper background to the left of the badge. */
    leadingGutter?: boolean;
    /** Compact variant only: 700 score and "Not scored" labels. */
    bold?: boolean;
  }>(),
  {
    jobTitle: '',
    hideJobTitle: false,
    shouldBgBeWhite: false,
    variant: 'stars',
    leadingGutter: false,
    bold: false,
  },
);

const hasScore = computed(() => hasApplicationScore(props.score));
const resolvedScore = computed<number | undefined>(() =>
  hasScore.value ? (props.score as number) : undefined,
);
const compactNotScoredBackgroundClass = computed(() => {
  if (props.shouldBgBeWhite) {
    return 'bg-tint-60';
  }

  return props.isViewed ? 'bg-tint-60' : 'bg-tint-80';
});

const compactCardBackgroundClass = computed(() => {
  if (props.shouldBgBeWhite || props.isSelected) {
    return 'bg-white';
  }

  return 'bg-tint-40';
});
</script>

<template>
  <div
    v-if="variant === 'compact'"
    class="inline-flex max-w-full items-center rounded py-0.5 pr-1"
    :class="[compactCardBackgroundClass, { 'pl-0.5': leadingGutter }]"
  >
    <ApplicationScoreCompactDisplay
      :score="score"
      :job-title="jobTitle"
      :hide-job-title="hideJobTitle"
      :not-scored-bg-class="compactNotScoredBackgroundClass"
      :bold="bold"
    />
  </div>
  <div
    v-else
    :class="{
      'score-card-wrapper inline-block flex w-fit rounded': true,
      'bg-highlight-50 bg-tint-0': isSelected && !shouldBgBeWhite,
      'bg-tint-40': !isSelected && !shouldBgBeWhite && !isViewed,
      'bg-tint-20': !isSelected && !shouldBgBeWhite && isViewed,
      'bg-white': shouldBgBeWhite,
    }"
  >
    <div
      class="score-card flex items-center"
      :class="[
        {
          'flex w-fit space-x-2 rounded': true,
          'bg-tint-0': isSelected && !shouldBgBeWhite,
          'bg-tint-40': !isSelected && !shouldBgBeWhite && !isViewed,
          'bg-tint-20': !isSelected && !shouldBgBeWhite && isViewed,
          'bg-white': shouldBgBeWhite,
        },
      ]"
    >
      <div
        v-if="jobTitle"
        :class="{
          'applicant-title ml-1 min-w-0 flex-1 truncate': true,
          'font-normal text-shade-840': isViewed,
          '!font-normal !text-shade-840': isViewed,
        }"
        :title="jobTitle"
      >
        {{ jobTitle }}
      </div>
      <score-rating
        v-if="hasScore"
        :width="16"
        :height="16"
        :score="resolvedScore"
        :is-viewed="isViewed"
      />
      <div v-else class="font-size-10 rounded-sm bg-shade-900 px-0.5">N/A</div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.applicant-title {
  max-width: 188px;
  line-height: 150%;
  font-size: 10px;
}
.score-card-wrapper {
  padding: 1px;
}
.score-card {
  padding: 3px;
}

.font-size-10 {
  font-size: 10px;
}
</style>
